/** Das aufgeschlagene Buch.
 *
 *  Hier wird alles gepflegt, was zu einem Band gehört: Inhaltsangabe, eigene
 *  Notizen, Bewertung, Lieblingsbuch, Status (inklusive „abgebrochen"),
 *  Schlagworte und Listen.
 *
 *  Zwei Regeln stehen sichtbar im Bau:
 *   • Keine Bewertung ist ein eigener, gleichwertiger Zustand — nicht „0".
 *   • Goodreads-Sterne bleiben getrennt und werden nicht hochgerechnet;
 *     Mario kann sie einmalig als eigene 0–10-Bewertung übernehmen.
 */

import { useEffect, useState } from "react";
import type { Book, Status, Tag } from "../../../shared/types";
import { STATUS_LABEL } from "../../../shared/types";
import { KIND_LABEL, VOCABULARY } from "../../../shared/vocabulary";
import * as api from "../lib/api";
import { useStore } from "../lib/store";

const STATUS_FOLGE: Status[] = ["read", "reading", "want", "dnf"];

function Bewertung({
  wert,
  onSetzen,
}: {
  wert: number | null;
  onSetzen: (w: number | null) => void;
}) {
  const [entwurf, setEntwurf] = useState<number>(wert ?? 7);
  useEffect(() => setEntwurf(wert ?? 7), [wert]);

  return (
    <div>
      <span className="feldname">Bewertung 0–10 (freiwillig)</span>
      {wert == null ? (
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <span className="lauftext" style={{ margin: 0, fontSize: 14 }}>
            Noch nicht bewertet — das ist kein schlechtes Zeichen.
          </span>
          <button className="knopf" onClick={() => onSetzen(entwurf)}>
            Jetzt bewerten
          </button>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
          <input
            className="regler"
            type="range"
            min={0}
            max={10}
            step={1}
            value={entwurf}
            style={{ ["--fuell" as string]: `${entwurf * 10}%` }}
            onChange={(e) => setEntwurf(Number(e.target.value))}
            onMouseUp={() => onSetzen(entwurf)}
            onTouchEnd={() => onSetzen(entwurf)}
            onKeyUp={() => onSetzen(entwurf)}
          />
          <strong
            style={{
              fontFamily: "var(--book)",
              fontSize: 26,
              minWidth: 58,
              textAlign: "right",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {entwurf}/10
          </strong>
          <button
            className="knopf"
            onClick={() => onSetzen(null)}
            title="Bewertung wieder entfernen"
          >
            zurücknehmen
          </button>
        </div>
      )}
    </div>
  );
}

function TagAuswahl({
  book,
  onGeaendert,
}: {
  book: Book;
  onGeaendert: (tags: Tag[]) => void;
}) {
  const { owner, schreibe } = useStore();
  const [offen, setOffen] = useState(false);
  const [frei, setFrei] = useState("");
  const vergeben = new Set((book.tags || []).map((t) => t.name));

  async function umschalten(name: string) {
    const drin = vergeben.has(name);
    const tags = await schreibe(() =>
      api.setTags(owner, book.id, drin ? [] : [name], drin ? [name] : []),
    );
    if (tags) onGeaendert(tags);
  }

  return (
    <div>
      <span className="feldname">Schlagworte</span>
      <div
        style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}
      >
        {(book.tags || []).map((t) => (
          <button
            key={t.id}
            className="chip an"
            onClick={() => umschalten(t.name)}
            title="entfernen"
          >
            {t.name} ×
          </button>
        ))}
        {!(book.tags || []).length && (
          <span className="lauftext" style={{ margin: 0, fontSize: 13.5 }}>
            Noch keine — über „Schlagworte wählen" oder die Verschlagwortung in
            der Werkstatt.
          </span>
        )}
      </div>
      <button className="knopf" onClick={() => setOffen((o) => !o)}>
        {offen ? "Auswahl schließen" : "Schlagworte wählen"}
      </button>
      {offen && (
        <div className="karte" style={{ marginTop: 10 }}>
          {(Object.keys(VOCABULARY) as (keyof typeof VOCABULARY)[]).map(
            (kind) => (
              <div key={kind} style={{ marginBottom: 14 }}>
                <span className="feldname">{KIND_LABEL[kind]}</span>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {VOCABULARY[kind].map((name) => (
                    <button
                      key={name}
                      className={`chip${vergeben.has(name) ? " an" : ""}`}
                      onClick={() => umschalten(name)}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              </div>
            ),
          )}
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              placeholder="Eigenes Schlagwort"
              value={frei}
              onChange={(e) => setFrei(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && frei.trim()) {
                  void umschalten(frei.trim());
                  setFrei("");
                }
              }}
            />
            <button
              className="knopf"
              disabled={!frei.trim()}
              onClick={() => {
                void umschalten(frei.trim());
                setFrei("");
              }}
            >
              Hinzufügen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function Detail({
  book: start,
  onSchliessen,
}: {
  book: Book;
  onSchliessen: () => void;
}) {
  const { owner, lists, schreibe, ersetze, neuLaden, ki, series } = useStore();
  const [book, setBook] = useState<Book>(start);
  const [notizen, setNotizen] = useState(start.notes ?? "");
  const [verlauf, setVerlauf] = useState<{ notes: string; saved_at: string }[]>(
    [],
  );
  const [laedtDetail, setLaedtDetail] = useState(true);

  useEffect(() => {
    let weg = false;
    (async () => {
      try {
        const res = await api.bookDetail(owner, start.id);
        if (weg) return;
        setBook(res.data.book);
        setNotizen(res.data.book.notes ?? "");
        setVerlauf(res.data.noteHistory);
      } catch {
        /* Vorschau-Modus: mit den vorhandenen Daten weiterarbeiten */
      } finally {
        if (!weg) setLaedtDetail(false);
      }
    })();
    return () => {
      weg = true;
    };
  }, [owner, start.id]);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onSchliessen();
    addEventListener("keydown", esc);
    return () => removeEventListener("keydown", esc);
  }, [onSchliessen]);

  async function speichern(patch: Partial<Book>, meldung?: string) {
    const neu = await schreibe(
      () => api.saveBook(owner, book.id, patch),
      meldung,
    );
    if (neu) {
      setBook(neu);
      ersetze(neu);
    }
  }

  const reihe = book.series
    ? series.find((s) => s.name === book.series)
    : undefined;
  const gross = book.cover
    ? api.coverUrl(book.cover_key ?? book.isbn, "m")
    : null;

  return (
    <div
      className="overlay"
      onClick={(e) => e.target === e.currentTarget && onSchliessen()}
    >
      <button className="schliessen" onClick={onSchliessen}>
        Schließen
      </button>
      <div className="blatt">
        <div className="gross">
          {gross ? (
            <img src={gross} alt={`Umschlag von ${book.title}`} />
          ) : (
            <div className="ohnebild">
              <b>{book.title}</b>
              <i>{(book.author || "").split(",")[0]}</i>
            </div>
          )}
          {book.also && (
            <p
              className="klein"
              style={{ marginTop: 12, color: "var(--brass)" }}
            >
              ◆ steht auch im anderen Regal
            </p>
          )}
        </div>

        <div>
          <h2>{book.title}</h2>
          <p className="klein" style={{ margin: "0 0 18px" }}>
            {book.author}
            {book.pub_year ? ` · ${book.pub_year}` : ""}
            {book.pages ? ` · ${book.pages} Seiten` : ""}
          </p>

          {reihe && (
            <p className="lauftext" style={{ fontSize: 14 }}>
              Reihe <strong>{reihe.name}</strong>
              {book.series_no != null ? `, Band ${book.series_no}` : ""} —{" "}
              {reihe.books.length} Bände im Regal
              {reihe.gaps.length ? (
                <span style={{ color: "var(--warn)" }}>
                  {" "}
                  · es fehlen Band {reihe.gaps.join(", ")}
                </span>
              ) : null}
            </p>
          )}

          {book.description ? (
            <p className="lauftext">{book.description}</p>
          ) : (
            <p
              className="lauftext"
              style={{ fontStyle: "italic", opacity: 0.72 }}
            >
              Noch keine Inhaltsangabe.{" "}
              <button
                className="knopf"
                style={{ marginLeft: 6 }}
                onClick={() =>
                  schreibe(() => api.enrichBook(owner, book.id)).then((neu) => {
                    if (neu) {
                      setBook(neu);
                      ersetze(neu);
                    }
                  })
                }
              >
                Nachladen{ki ? "" : " (ohne KI: nur Open Library)"}
              </button>
            </p>
          )}

          <hr className="trennlinie" />

          <div style={{ display: "grid", gap: 20 }}>
            <div>
              <span className="feldname">Status</span>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {STATUS_FOLGE.map((s) => (
                  <button
                    key={s}
                    className={`chip${book.status === s ? " an" : ""}`}
                    onClick={() => speichern({ status: s })}
                  >
                    {STATUS_LABEL[s]}
                  </button>
                ))}
                <button
                  className={`chip${book.fav ? " an" : ""}`}
                  onClick={() => speichern({ fav: book.fav ? 0 : 1 })}
                >
                  ★ Lieblingsbuch
                </button>
              </div>
              {book.status === "dnf" && (
                <p
                  className="lauftext"
                  style={{ fontSize: 13, marginTop: 8, opacity: 0.75 }}
                >
                  Abgebrochen zählt als Hinweis für Empfehlungen — es ist keine
                  schlechte Note.
                </p>
              )}
            </div>

            <Bewertung
              wert={book.rating}
              onSetzen={(w) => speichern({ rating: w })}
            />

            {book.stars != null && (
              <div>
                <span className="feldname">Aus Goodreads übernommen</span>
                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <span style={{ fontFamily: "var(--book)", fontSize: 18 }}>
                    {"★".repeat(Math.round(book.stars))}
                    <span style={{ opacity: 0.3 }}>
                      {"★".repeat(5 - Math.round(book.stars))}
                    </span>{" "}
                    <span className="klein">{book.stars} von 5</span>
                  </span>
                  {book.rating == null && (
                    <button
                      className="knopf"
                      title="Die Sterne werden nicht hochgerechnet — du setzt die Zahl selbst."
                      onClick={() =>
                        speichern({ rating: Math.round(book.stars! * 2) })
                      }
                    >
                      als {Math.round(book.stars * 2)}/10 übernehmen
                    </button>
                  )}
                </div>
              </div>
            )}

            <div>
              <span className="feldname">Deine Notizen</span>
              <textarea
                rows={5}
                value={notizen}
                placeholder="Was ist geblieben? Lieblingsstellen, an wen es sich weitergeben lässt …"
                onChange={(e) => setNotizen(e.target.value)}
                onBlur={() => {
                  if ((book.notes ?? "") !== notizen)
                    speichern({ notes: notizen }, "Notiz gespeichert.");
                }}
              />
              {verlauf.length > 0 && (
                <details style={{ marginTop: 8 }}>
                  <summary className="klein" style={{ cursor: "pointer" }}>
                    {verlauf.length} frühere Fassungen
                  </summary>
                  {verlauf.map((v) => (
                    <div
                      key={v.saved_at}
                      className="karte"
                      style={{ marginTop: 8 }}
                    >
                      <p className="lauftext" style={{ fontSize: 14 }}>
                        {v.notes}
                      </p>
                      <div
                        style={{
                          display: "flex",
                          gap: 10,
                          alignItems: "center",
                        }}
                      >
                        <span className="klein">{v.saved_at}</span>
                        <button
                          className="knopf"
                          onClick={() =>
                            schreibe(() =>
                              api.restoreNotes(owner, book.id, v.saved_at),
                            ).then((neu) => {
                              if (neu) {
                                setBook(neu);
                                setNotizen(neu.notes ?? "");
                                ersetze(neu);
                              }
                            })
                          }
                        >
                          zurückholen
                        </button>
                      </div>
                    </div>
                  ))}
                </details>
              )}
            </div>

            <TagAuswahl
              book={book}
              onGeaendert={(tags) => {
                const neu = { ...book, tags };
                setBook(neu);
                ersetze(neu);
              }}
            />

            {lists.length > 0 && (
              <div>
                <span className="feldname">Listen</span>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {lists.map((l) => {
                    const drin = (book.lists || []).includes(l.id);
                    return (
                      <button
                        key={l.id}
                        className={`chip${drin ? " an" : ""}`}
                        onClick={async () => {
                          const res = await schreibe(() =>
                            api.setListMember(owner, l.id, book.id, !drin),
                          );
                          if (res !== null) {
                            const listen = drin
                              ? (book.lists || []).filter((x) => x !== l.id)
                              : [...(book.lists || []), l.id];
                            const neu = { ...book, lists: listen };
                            setBook(neu);
                            ersetze(neu);
                          }
                        }}
                      >
                        {l.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <span className="feldname">Lesejahr</span>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <input
                  style={{ width: 120 }}
                  inputMode="numeric"
                  defaultValue={book.read_year ?? ""}
                  placeholder="z. B. 2025"
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v !== (book.read_year ?? ""))
                      speichern({ read_year: v || null });
                  }}
                />
                <span className="klein" style={{ opacity: 0.6 }}>
                  {laedtDetail
                    ? "lädt …"
                    : book.updated_at
                      ? `zuletzt geändert ${book.updated_at}`
                      : ""}
                </span>
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button
                className="knopf"
                onClick={() =>
                  schreibe(() => api.enrichBook(owner, book.id)).then((neu) => {
                    if (neu) {
                      setBook(neu);
                      ersetze(neu);
                    }
                  })
                }
              >
                Angaben ergänzen
              </button>
              <button
                className="knopf"
                style={{
                  borderColor: "rgba(217,112,102,0.5)",
                  color: "#f0bdb7",
                }}
                onClick={async () => {
                  const res = await schreibe(() =>
                    api.removeBook(owner, book.id),
                  );
                  if (res !== null) {
                    await neuLaden();
                    onSchliessen();
                  }
                }}
              >
                In den Papierkorb
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
